import React from "react";
import { CircleAlertIcon, DollarSignIcon } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
} from "@/components/ui";
import { useTranslate } from "@/hooks";
import { Button } from "@/components/main";
import { formatCurrency } from "@/helpers";
import { cn } from "@/helpers/tailwind";
import isNil from "lodash.isnil";

interface ConfirmSelectionDialogProps {
  open: boolean;
  artist?: {
    name: string;
    image: string;
  };
  amount: number;
  potentialProfit: { amount: number; percentage: number };
  loading: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: () => void;
}

const ConfirmSelectionDialog: React.FC<ConfirmSelectionDialogProps> = ({
  open,
  artist,
  amount,
  potentialProfit,
  loading,
  setOpen,
  onConfirm,
}) => {
  const t = useTranslate();

  if (isNil(artist)) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md p-6">
        <DialogHeader>
          <div className="mb-4 flex items-center">
            <DialogTitle>Confirm Your Selection</DialogTitle>
          </div>
          <DialogDescription className="mt-3 font-geist font-light">
            You're about to support{" "}
            <span className="font-bold">{artist.name}</span>. Are you sure you
            want to proceed?
          </DialogDescription>
        </DialogHeader>

        <div>
          <Separator
            orientation="horizontal"
            className="mb-3 h-0 w-full border-t border-dashed border-slate-700"
          />
          <div className="flex items-center justify-between gap-4">
            <img
              src={artist.image}
              alt={artist.name}
              className="size-14 rounded-lg"
            />
            <div className="flex-grow">
              <p className="mb-1 text-sm">Amount:</p>
              <p className="text-base font-bold">{`$ ${amount.toString()}`}</p>
            </div>

            <div
              className={cn(
                "rounded-md bg-green-400/15 p-2 text-center text-green-300",
                "flex flex-shrink flex-col gap-1"
              )}>
              <span className="text-xs">Potential profit</span>
              <div className="flex items-center justify-center gap-1 text-[11px] font-semibold">
                <span>$ {potentialProfit.amount.toFixed(2)}</span>
                <span className="h-3 w-0 border border-r-green-300" />
                <span>{potentialProfit.percentage.toFixed(0)}%</span>
              </div>
            </div>
          </div>
          <Separator
            orientation="horizontal"
            className="mt-3 h-0 w-full border-t border-dashed border-slate-700"
          />
        </div>

        {/* Disclaimer */}
        <div className="text-white">
          <span className="mb-2 flex items-center gap-2">
            <CircleAlertIcon className="h-4 w-4" />
            <span className="font-geist text-sm">
              {t("components.disclaimer")}
            </span>
          </span>
          <p className="text-sm font-light text-slate-400">
            {t("components.disclaimer_description")}
          </p>
        </div>

        <DialogFooter className="mt-4 justify-end">
          <DialogClose asChild>
            <Button
              variant="outline"
              className="border-slate-200 bg-transparent hover:bg-transparent">
              {t("common.other.cancel")}
            </Button>
          </DialogClose>
          <Button variant="tertiary" onClick={onConfirm} loading={loading}>
            <DollarSignIcon />
            <span>Buy</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmSelectionDialog;
